import { Order, OrderStatus, OrderType, TransactionType } from '../proto/generated/order';
import { calculateGcd, ORDER_TYPES_STRATEGY_AUXILIARY } from './orders';

export function effectiveOrderFilledQuantity(o: Order): number {
    const q = o.quantity ?? 0;
    const fq = o.filledQuantity ?? 0;
    if (fq > 0) return fq;
    if (o.status === OrderStatus.EXECUTED && q > 0) return q;
    return 0;
}

export const ICEBERG_NON_TERMINAL_STATUSES: OrderStatus[] = [
    OrderStatus.OPEN,
    OrderStatus.PENDING,
    OrderStatus.PARTIALLY_FILLED,
    OrderStatus.INACTIVE,
    OrderStatus.TRIGGER_PENDING,
];

/** True when an order carries indexed iceberg leg metadata. */
export function isIndexedIcebergLeg(o: Order): boolean {
    return Boolean(
        o.orderExecutionParams?.icebergNumberOfLegs &&
        o.orderExecutionParams?.icebergLegNumber != null
    );
}

/** Non-basket iceberg primary leg (excludes sibling SL/TP/CE/CX rows). */
export function isNonBasketIcebergLeg(o: Order): boolean {
    return !o.isBasket && o.orderType === OrderType.ICEBERG && isIndexedIcebergLeg(o);
}

/** Non-basket iceberg sibling protective rows (kept separate from primary iceberg aggregation). */
export function isNonBasketIcebergSiblingSlTp(o: Order): boolean {
    return !o.isBasket && (o.orderType === OrderType.STOP_LOSS || o.orderType === OrderType.TARGET);
}

/**
 * Basket SL/TP on the same batch as a basket-iceberg ladder (no indexed iceberg params).
 * Shown only when the basket iceberg row is expanded; excluded from group totals.
 */
export function isBasketIcebergBatchSlTpAux(o: Order): boolean {
    return Boolean(
        o.isBasket &&
            o.basketId &&
            o.batchId &&
            (o.orderType === OrderType.STOP_LOSS || o.orderType === OrderType.TARGET) &&
            !o.orderExecutionParams?.icebergNumberOfLegs
    );
}

/**
 * Basket strategy aux legs (SL/TP/CE/CX) without indexed iceberg sizing — tuck under basket-iceberg expand by {@link basketId}.
 */
export function isBasketIcebergBatchStrategyAux(o: Order): boolean {
    return Boolean(
        o.isBasket &&
            o.basketId &&
            o.batchId &&
            ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType) &&
            !o.orderExecutionParams?.icebergNumberOfLegs
    );
}

function isStrategyBasketId(basketId: string | undefined): boolean {
    return Boolean(basketId && basketId !== 'default' && !basketId.startsWith('default_'));
}

/** Canonical non-basket iceberg group key: batch + security only (no basketId; matches Android). */
export function nonBasketIcebergGroupKey(o: Pick<Order, 'batchId' | 'security'>): string | undefined {
    if (!o.batchId) return undefined;
    const securityCode = o.security?.code ?? '';
    const securityExchange = o.security?.exchange ?? 0;
    return `${o.batchId}_${securityExchange}_${securityCode}`;
}

const NON_TERMINAL_PARENT_STATUS_PRIORITY: Partial<Record<OrderStatus, number>> = {
    [OrderStatus.OPEN]: 0,
    [OrderStatus.PARTIALLY_FILLED]: 1,
    [OrderStatus.TRIGGER_PENDING]: 2,
    [OrderStatus.PENDING]: 3,
    [OrderStatus.INACTIVE]: 4,
};

export function pickRepresentativeNonTerminalIcebergLeg(orders: Order[]): Order | undefined {
    const candidates = orders.filter((o) =>
        ICEBERG_NON_TERMINAL_STATUSES.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
    );
    if (!candidates.length) return undefined;
    return candidates.reduce((best, o) => {
        const p = NON_TERMINAL_PARENT_STATUS_PRIORITY[o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN] ?? 50;
        const bp = NON_TERMINAL_PARENT_STATUS_PRIORITY[best.status ?? OrderStatus.ORDER_STATUS_UNKNOWN] ?? 50;
        return p < bp ? o : best;
    });
}

export function getIcebergLegOrdersForSecurity(
    orders: Order[],
    batchId: string | undefined,
    basketId: string | undefined,
    security: { code?: string; exchange?: number } | undefined
): Order[] {
    if (!batchId || !basketId || !security?.code) return [];
    return orders
        .filter(
            (o) =>
                o.batchId === batchId &&
                o.basketId === basketId &&
                o.security?.code === security.code &&
                o.security?.exchange === security.exchange &&
                o.orderExecutionParams?.icebergNumberOfLegs
        )
        .sort((a, b) => {
            const ln =
                (a.orderExecutionParams?.icebergLegNumber ?? 0) -
                (b.orderExecutionParams?.icebergLegNumber ?? 0);
            if (ln !== 0) return ln;
            return (a.created ?? 0) - (b.created ?? 0);
        });
}

export function hasPlacedIcebergLegsForBatch(
    orders: Order[] | undefined,
    batchId: string | undefined,
    basketId: string | undefined
): boolean {
    if (!basketId || !batchId || !orders?.length) return false;
    return orders.some(
        (o) =>
            o.basketId === basketId &&
            o.batchId === batchId &&
            o.orderExecutionParams?.icebergNumberOfLegs != null
    );
}

const icebergBasketSecurityKey = (s: { code?: string; exchange?: number }) =>
    `${s.exchange ?? 0}_${s.code ?? ''}`;

export type IcebergBasketTradeLine = {
    selected: boolean;
    quantity: number;
    transactionType: TransactionType;
    security: { code?: string; exchange?: number };
};

export function icebergBasketStrategyDisplayQuantity(
    placedOrders: Order[] | undefined,
    batchId: string | undefined,
    basketId: string | undefined,
    tradeLines: IcebergBasketTradeLine[]
): number | null {
    if (!batchId || !basketId || !placedOrders?.length || !tradeLines?.length) return null;
    if (!hasPlacedIcebergLegsForBatch(placedOrders, batchId, basketId)) return null;

    const selected = tradeLines.filter((o) => o.selected);
    if (!selected.length) return null;

    const keys = [...new Set(selected.map((o) => icebergBasketSecurityKey(o.security)))];
    const completedPerSecurity: number[] = [];
    for (const k of keys) {
        const row = selected.find((o) => icebergBasketSecurityKey(o.security) === k);
        if (!row?.security?.code) return null;
        const legs = getIcebergLegOrdersForSecurity(placedOrders, batchId, basketId, row.security);
        if (legs.length === 0) return null;
        completedPerSecurity.push(legs.filter((l) => l.status === OrderStatus.EXECUTED).length);
    }
    const minComplete = Math.min(...completedPerSecurity);
    if (minComplete < 1) return null;

    const gcdUnit = calculateGcd(selected.map((o) => Math.abs(o.quantity ?? 0)));
    if (!gcdUnit) return null;

    const sliceNet = selected.reduce(
        (s, o) => s + o.quantity * (o.transactionType === TransactionType.BUY ? 1 : -1),
        0
    );
    const sign = Math.sign(sliceNet) || 1;
    return sign * minComplete * gcdUnit;
}

const ICEBERG_BATCH_WORK_TERMINAL: OrderStatus[] = [
    OrderStatus.EXECUTED,
    OrderStatus.CANCELLED,
    OrderStatus.REJECTED,
    OrderStatus.FAILED,
];

export function hasIncompleteIcebergOrdersInBatch(
    orders: Order[] | undefined,
    batchId: string | undefined,
    basketId: string | undefined
): boolean {
    if (!basketId || !batchId || !orders?.length) return false;
    return orders.some((o) => {
        if (o.basketId !== basketId || o.batchId !== batchId) return false;
        if (o.orderExecutionParams?.icebergNumberOfLegs == null) return false;
        return !ICEBERG_BATCH_WORK_TERMINAL.includes(o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN);
    });
}

export function icebergLegsHavePendingNonTerminal(legs: Order[]): boolean {
    return legs.some((l) => ICEBERG_NON_TERMINAL_STATUSES.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN));
}

export function icebergLegsAllExecuted(legs: Order[]): boolean {
    return legs.length > 0 && legs.every((l) => l.status === OrderStatus.EXECUTED);
}
export function icebergLadderIncompleteForSecurity(legs: Order[]): boolean {
    if (!legs.length) return false;
    const n = legs[0]?.orderExecutionParams?.icebergNumberOfLegs ?? 1;
    if (
        legs.some(
            (l) =>
                !ICEBERG_BATCH_WORK_TERMINAL.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
        )
    ) {
        return true;
    }
    const executedLegCount = legs.filter((l) => l.status === OrderStatus.EXECUTED).length;
    return executedLegCount < n;
}

const icebergLegStatusDone = (s: OrderStatus | undefined) =>
    s === OrderStatus.EXECUTED ||
    s === OrderStatus.CANCELLED ||
    s === OrderStatus.FAILED ||
    s === OrderStatus.REJECTED;

export type IcebergLegUi = {
    k: number;
    n: number;
    allDone: boolean;
    filled: number;
    legQty: number;
};

export function getIcebergLegUiFromLegs(legs: Order[], numberOfLegsFallback = 1): IcebergLegUi | null {
    if (!legs.length) return null;
    const sorted = [...legs].sort((a, b) => {
        const ln =
            (a.orderExecutionParams?.icebergLegNumber ?? 0) - (b.orderExecutionParams?.icebergLegNumber ?? 0);
        if (ln !== 0) return ln;
        return (a.created ?? 0) - (b.created ?? 0);
    });
    const n =
        sorted[0]?.orderExecutionParams?.icebergNumberOfLegs ?? Math.max(1, numberOfLegsFallback);
    const current = sorted.find((l) => !icebergLegStatusDone(l.status));
    if (current) {
        const k = current.orderExecutionParams?.icebergLegNumber ?? 1;
        return {
            k,
            n,
            allDone: false,
            filled: effectiveOrderFilledQuantity(current),
            legQty: current.quantity,
        };
    }
    return { k: n, n, allDone: true, filled: 0, legQty: 0 };
}

export function getCancellableIcebergLegIds(legs: Order[]): string[] {
    return legs
        .filter(
            (o) =>
                o.orderId &&
                o.status !== OrderStatus.EXECUTED &&
                o.status !== OrderStatus.CANCELLED &&
                o.status !== OrderStatus.FAILED &&
                o.status !== OrderStatus.REJECTED
        )
        .map((o) => o.orderId!);
}

/** Expandable batch in orders UI (basket iceberg ladder or full strategy basket without iceberg). */
export interface OrderBatchGroup {
    isIcebergGroup: true;
    allOrders: Order[];
    totalQuantity: number;
    totalFilledQuantity: number;
    totalLegs: number;
    executedLegs: number;
    batchId: string;
    basketId: string;
    representativeOrder?: Order;
    isBasketGroup?: boolean;
    securitiesCount?: number;
    expanded?: boolean;
    /** True when grouped from all `isBasket` legs with no iceberg-indexed legs in the batch (non-iceberg strategy basket). */
    strategyBasketOnly?: boolean;
    /** Basket SL/TP/CE/CX (strategy aux, no iceberg sizing); expand-only; excluded from group totals. */
    basketAuxiliaryOrders?: Order[];
}

/** @deprecated Use OrderBatchGroup */
export type IcebergOrderGroup = OrderBatchGroup;

export type OrderOrBatchGroup = Order | OrderBatchGroup;
/** @deprecated Use OrderOrBatchGroup */
export type OrderOrIcebergGroup = OrderOrBatchGroup;

export function isIcebergGroup(item: OrderOrIcebergGroup): item is OrderBatchGroup {
    return (item as OrderBatchGroup).isIcebergGroup === true;
}

function isBasketBatchIcebergOrder(order: Order): boolean {
    return Boolean(
        order.orderExecutionParams?.icebergNumberOfLegs &&
        order.batchId &&
        order.basketId
    );
}

export function groupIcebergOrders(orders: Order[]): OrderOrBatchGroup[] {
    const icebergGroups = new Map<string, Order[]>();

    // First pass: identify and group iceberg orders
    orders.forEach(order => {
        if (isBasketBatchIcebergOrder(order)) {
            const securityCode = order.security?.code ?? '';
            const securityExchange = order.security?.exchange ?? 0;
            const groupKey = `${order.basketId}_${order.batchId}_${securityExchange}_${securityCode}`;
            if (!icebergGroups.has(groupKey)) {
                icebergGroups.set(groupKey, []);
            }
            icebergGroups.get(groupKey)!.push(order);
        }
    });

    // Second pass: create iceberg groups and combine with regular orders
    const result: OrderOrBatchGroup[] = [];
    const processedGroups = new Set<string>();
    const addedRegularKeys = new Set<string>();

    orders.forEach(order => {
        // Handle both basket iceberg orders (isBasket: true) and single iceberg orders (isBasket: false)
        if (isBasketBatchIcebergOrder(order)) {
            const securityCode = order.security?.code ?? '';
            const securityExchange = order.security?.exchange ?? 0;
            const groupKey = `${order.basketId}_${order.batchId}_${securityExchange}_${securityCode}`;
            
            // Only process each group once
            if (!processedGroups.has(groupKey)) {
                processedGroups.add(groupKey);
                const groupOrders = icebergGroups.get(groupKey)!;
                
                // Sort by leg number
                groupOrders.sort((a, b) => {
                    const ln =
                        (a.orderExecutionParams?.icebergLegNumber ?? 0) -
                        (b.orderExecutionParams?.icebergLegNumber ?? 0);
                    if (ln !== 0) return ln;
                    return (a.created ?? 0) - (b.created ?? 0);
                });

                const totalQuantity = groupOrders.reduce((sum, o) => sum + o.quantity, 0);
                const totalFilledQuantity = groupOrders.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
                const executedLegs = groupOrders.filter(o => o.status === OrderStatus.EXECUTED).length;

                result.push({
                    isIcebergGroup: true,
                    allOrders: groupOrders,
                    totalQuantity,
                    totalFilledQuantity,
                    totalLegs: order.orderExecutionParams?.icebergNumberOfLegs ?? groupOrders.length,
                    executedLegs,
                    batchId: order.batchId, // Same batchId for all legs
                    basketId: order.basketId // Same basketId for all legs
                });
            }
        } else {
            const regKey = order.orderId || order.clientOrderId || `${order.batchId}_${order.security?.code}`;
            if (!addedRegularKeys.has(regKey)) {
                addedRegularKeys.add(regKey);
                result.push(order);
            }
        }
    });

    return result;
}

function buildStrategyBasketOnlyBatchGroup(groupOrders: Order[]): OrderBatchGroup {
    const sorted = [...groupOrders].sort((a, b) => (a.created ?? 0) - (b.created ?? 0));
    const uniqueSecurities = new Set(
        sorted.map((o) => `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}`)
    );
    const totalQuantity = sorted.reduce((sum, o) => sum + (o.quantity ?? 0), 0);
    const totalFilledQuantity = sorted.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
    const executedLegs = sorted.filter((o) => o.status === OrderStatus.EXECUTED).length;
    const first = sorted[0];
    return {
        isIcebergGroup: true,
        allOrders: sorted,
        totalQuantity,
        totalFilledQuantity,
        totalLegs: sorted.length,
        executedLegs,
        batchId: first.batchId ?? '',
        basketId: first.basketId ?? '',
        isBasketGroup: true,
        securitiesCount: uniqueSecurities.size,
        strategyBasketOnly: true,
    }
}

export function groupIcebergBasketOrders(orders: Order[]): OrderOrBatchGroup[] {
    const basketGroups = new Map<string, Order[]>();
    const nonBasketIcebergGroups = new Map<string, Order[]>();
    /** All `isBasket` orders for a basket, across placement waves (`batchId`). */
    const strategyBasketMap = new Map<string, Order[]>();
    /** Basket has at least one iceberg-sized leg (indexed ladder). */
    const basketBatchHasIceberg = new Set<string>();

    // First pass: identify iceberg orders and separate basket vs non-basket
    orders.forEach(order => {
        if (order.isBasket && order.basketId && order.batchId) {
            const bid = order.basketId;
            if (!strategyBasketMap.has(bid)) strategyBasketMap.set(bid, []);
            strategyBasketMap.get(bid)!.push(order);
            if (order.orderExecutionParams?.icebergNumberOfLegs) {
                basketBatchHasIceberg.add(bid);
            }
        }
        if (order.isBasket && order.basketId && order.batchId && order.orderExecutionParams?.icebergNumberOfLegs) {
            const bid = order.basketId;
            if (!basketGroups.has(bid)) {
                basketGroups.set(bid, []);
            }
            basketGroups.get(bid)!.push(order);
        } else if (isNonBasketIcebergLeg(order)) {
            const nonBasketGroupKey = nonBasketIcebergGroupKey(order);
            if (!nonBasketGroupKey) return;
            if (!nonBasketIcebergGroups.has(nonBasketGroupKey)) {
                nonBasketIcebergGroups.set(nonBasketGroupKey, []);
            }
            nonBasketIcebergGroups.get(nonBasketGroupKey)!.push(order);
        }
    });

    // Second pass: create groups
    const result: OrderOrBatchGroup[] = [];
    const processedBasketGroups = new Set<string>();
    const processedNonBasketGroups = new Set<string>();
    const processedStrategyBasketOnly = new Set<string>();
    const addedRegularKeys = new Set<string>();
    /** IDs of basket SL/TP tucked under basket-iceberg expand (omit from top-level list). */
    const consumedBasketIcebergAuxIds = new Set<string>();

    orders.forEach(order => {
        if (order.isBasket && order.basketId && order.batchId && order.orderExecutionParams?.icebergNumberOfLegs) {
            // Handle basket iceberg orders (expandable) — one UI group per basketId.
            const bid = order.basketId;

            if (!processedBasketGroups.has(bid)) {
                processedBasketGroups.add(bid);
                const groupOrders = basketGroups.get(bid)!;
                
                // Get unique securities count
                const uniqueSecurities = new Set(groupOrders.map(o => 
                    `${o.security?.exchange}_${o.security?.code}`
                ));
                
                // Sort by security and leg number
                groupOrders.sort((a, b) => {
                    const securityDiff = (a.security?.code ?? '').localeCompare(b.security?.code ?? '');
                    if (securityDiff !== 0) return securityDiff;
                    const ln =
                        (a.orderExecutionParams?.icebergLegNumber ?? 0) -
                        (b.orderExecutionParams?.icebergLegNumber ?? 0);
                    if (ln !== 0) return ln;
                    return (a.created ?? 0) - (b.created ?? 0);
                });

                // Calculate totals across all securities and legs
                const totalQuantity = groupOrders.reduce((sum, o) => sum + o.quantity, 0);
                const totalFilledQuantity = groupOrders.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
                const executedLegs = groupOrders.filter(o => o.status === OrderStatus.EXECUTED).length;
                
                // For iceberg orders, multiply legs by securities
                const icebergLegs = groupOrders[0]?.orderExecutionParams?.icebergNumberOfLegs ?? 1;
                const totalLegs = icebergLegs * uniqueSecurities.size;

                const basketAuxiliaryOrders = orders
                    .filter((o) => isBasketIcebergBatchStrategyAux(o) && o.basketId === bid)
                    .sort((a, b) => (a.created ?? 0) - (b.created ?? 0));
                basketAuxiliaryOrders.forEach((o) => {
                    if (o.orderId) consumedBasketIcebergAuxIds.add(o.orderId);
                    if (o.clientOrderId) consumedBasketIcebergAuxIds.add(o.clientOrderId);
                });

                const basketGroup: OrderBatchGroup = {
                    isIcebergGroup: true,
                    allOrders: groupOrders,
                    totalQuantity,
                    totalFilledQuantity,
                    totalLegs,
                    executedLegs,
                    batchId: order.batchId,
                    basketId: order.basketId,
                    isBasketGroup: true, // Expandable basket group
                    securitiesCount: uniqueSecurities.size,
                    basketAuxiliaryOrders:
                        basketAuxiliaryOrders.length > 0 ? basketAuxiliaryOrders : undefined,
                };
                result.push(basketGroup);
            }
        } else if (isNonBasketIcebergLeg(order)) {
            // Handle non-basket iceberg orders (non-expandable single row)
            const nonBasketGroupKey = nonBasketIcebergGroupKey(order);
            if (!nonBasketGroupKey) return;
            
            if (!processedNonBasketGroups.has(nonBasketGroupKey)) {
                processedNonBasketGroups.add(nonBasketGroupKey);
                const groupOrders = nonBasketIcebergGroups.get(nonBasketGroupKey)!;
                
                groupOrders.sort((a, b) => {
                    const ln =
                        (a.orderExecutionParams?.icebergLegNumber ?? 0) -
                        (b.orderExecutionParams?.icebergLegNumber ?? 0);
                    if (ln !== 0) return ln;
                    return (a.created ?? 0) - (b.created ?? 0);
                });

                const totalQuantity = groupOrders.reduce((sum, o) => sum + o.quantity, 0);
                const totalFilledQuantity = groupOrders.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
                const executedLegs = groupOrders.filter(o => o.status === OrderStatus.EXECUTED).length;

                const nonBasketGroup: OrderBatchGroup = {
                    isIcebergGroup: true,
                    allOrders: groupOrders,
                    totalQuantity,
                    totalFilledQuantity,
                    totalLegs: order.orderExecutionParams?.icebergNumberOfLegs ?? groupOrders.length,
                    executedLegs,
                    batchId: order.batchId,
                    basketId: order.basketId || '', // May be empty for non-basket
                    isBasketGroup: false, // Non-expandable single row
                    securitiesCount: 1
                };
                result.push(nonBasketGroup);
            }
        } else {
            if (order.isBasket && order.basketId && order.batchId) {
                const bid = order.basketId;
                if (basketBatchHasIceberg.has(bid)) {
                    // Basket-iceberg aux / extra strategy rows should only appear inside expanded group.
                    if (isBasketIcebergBatchStrategyAux(order)) {
                        return;
                    }
                    const oid = order.orderId || order.clientOrderId;
                    if (oid && consumedBasketIcebergAuxIds.has(oid)) {
                        return;
                    }
                    if (isStrategyBasketId(bid)) {
                        return;
                    }
                    const regKey = order.orderId || order.clientOrderId || `${order.batchId}_${order.security?.code}`;
                    if (!addedRegularKeys.has(regKey)) {
                        addedRegularKeys.add(regKey);
                        result.push(order);
                    }
                } else if (!processedStrategyBasketOnly.has(bid)) {
                    processedStrategyBasketOnly.add(bid);
                    const batchOrders = strategyBasketMap.get(bid) ?? [order];
                    result.push(buildStrategyBasketOnlyBatchGroup(batchOrders));
                }
            } else if (
                order.basketId &&
                isStrategyBasketId(order.basketId) &&
                strategyBasketMap.has(order.basketId)
            ) {
                // SL/TP trigger fills share the strategy basketId but are not isBasket legs —
                // keep them only inside the expanded basket group.
                return;
            } else {
                const regKey = order.orderId || order.clientOrderId || `${order.batchId}_${order.security?.code}`;
                if (!addedRegularKeys.has(regKey)) {
                    addedRegularKeys.add(regKey);
                    result.push(order);
                }
            }
        }
    });

    return result;
}

export function getIcebergOrderIds(group: OrderBatchGroup): string[] {
    return group.allOrders
        .map(o => o.orderId)
        .filter((id): id is string => !!id);
}

/** Indexed basket iceberg legs only — all waves under `basketId` (SL/TP/CX/CE aux excluded by type). */
export function getIcebergBasketOrderIds(orders: Order[], basketId: string, _batchId?: string): string[] {
    if (!basketId) return [];
    return orders
        .filter(
            (o) =>
                o.basketId === basketId &&
                o.orderType === OrderType.ICEBERG &&
                Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                o.status !== OrderStatus.EXECUTED &&
                o.status !== OrderStatus.CANCELLED &&
                o.status !== OrderStatus.FAILED &&
                o.status !== OrderStatus.REJECTED &&
                Boolean(o.orderId)
        )
        .map((o) => o.orderId!);
}

/** All cancellable orders for a basket (every `batchId` wave: main, CE/CX, SL/TP). */
export function getCancellableBasketOrderIds(orders: Order[], basketId: string): string[] {
    if (!basketId) return [];
    return orders
        .filter(
            (o) =>
                o.basketId === basketId &&
                o.orderId &&
                o.status !== OrderStatus.EXECUTED &&
                o.status !== OrderStatus.CANCELLED &&
                o.status !== OrderStatus.FAILED &&
                o.status !== OrderStatus.REJECTED
        )
        .map((o) => o.orderId!);
}

/** @deprecated Use {@link getCancellableBasketOrderIds}; `batchId` is ignored (basket-scoped cancel). */
export function getCancellableBasketBatchOrderIds(orders: Order[], basketId: string, _batchId?: string): string[] {
    return getCancellableBasketOrderIds(orders, basketId);
}

/** True when the basket has indexed iceberg ladder legs (any placement wave). */
export function isIcebergBatch(orders: Order[], basketId: string, _batchId?: string): boolean {
    return orders.some(
        (o) =>
            o.basketId === basketId &&
            o.orderExecutionParams?.icebergNumberOfLegs &&
            o.orderExecutionParams?.icebergLegNumber
    );
}

export function filterOrdersOpenTabWide(orders: Order[]): Order[] {
    if (!orders?.length) return [];
    const openActiveStatuses: OrderStatus[] = [
        OrderStatus.OPEN,
        OrderStatus.PENDING,
        OrderStatus.PARTIALLY_FILLED,
        OrderStatus.TRIGGER_PENDING,
        OrderStatus.INACTIVE,
    ];
    const terminalSidebar = [OrderStatus.REJECTED, OrderStatus.CANCELLED, OrderStatus.FAILED];

    // 1) Basket rows: include full basket if any leg is open-active.
    const qualifyingBasketIds = new Set<string>();
    // 2) Standalone iceberg rows: include full batch if any leg is open-active.
    const qualifyingStandaloneIcebergBatchIds = new Set<string>();

    for (const o of orders) {
        const st = o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN;
        if (!openActiveStatuses.includes(st)) continue;

        if (o.isBasket && o.basketId) {
            qualifyingBasketIds.add(o.basketId);
            continue;
        }

        const isStandaloneIceberg =
            !o.isBasket &&
            o.orderType === OrderType.ICEBERG &&
            Boolean(o.batchId) &&
            Boolean(o.orderExecutionParams?.icebergNumberOfLegs);

        if (isStandaloneIceberg && o.batchId) {
            qualifyingStandaloneIcebergBatchIds.add(o.batchId);
        }
    }

    return orders.filter((o) => {
        const st = o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN;
        if (terminalSidebar.includes(st)) return false;

        // Basket logic
        if (o.isBasket && o.basketId) {
            return qualifyingBasketIds.has(o.basketId);
        }

        // Standalone iceberg logic
        const isStandaloneIceberg =
            !o.isBasket &&
            o.orderType === OrderType.ICEBERG &&
            Boolean(o.batchId) &&
            Boolean(o.orderExecutionParams?.icebergNumberOfLegs);

        if (isStandaloneIceberg && o.batchId) {
            return qualifyingStandaloneIcebergBatchIds.has(o.batchId);
        }

        // 3) Normal non-basket: strict per-order status
        return openActiveStatuses.includes(st);
    });
}